import type { JSX } from 'react';

import type { BuilderStore } from '../state.js';

import { ToggleField } from './Field.js';

export interface SignageEditorProps {
  store: BuilderStore;
}

const MIN_SECONDS = 10;
const MAX_SECONDS = 3600;
const DEFAULT_SECONDS = 60;

/**
 * Signage section — currently one knob, profile auto-rotation. Lives
 * in its own section rather than under Features because it's a value
 * (rotation interval, in seconds), not a boolean — the wrong shape
 * for the Features tab's flag-toggle grid.
 *
 * The runtime gates rotation on the `signageMode` feature flag too,
 * so the editor surfaces a hint when `signageMode` isn't in
 * `enabledFeatures`: the field still saves, but it won't take effect
 * on-device until the flag is on.
 */
export function SignageEditor({ store }: SignageEditorProps): JSX.Element {
  const signage = store.config.signage;
  const rotationOn = typeof signage?.rotationIntervalSeconds === 'number';
  const interval = signage?.rotationIntervalSeconds ?? DEFAULT_SECONDS;
  const profileCount = store.config.profiles.length;
  const signageFlagOn = store.config.enabledFeatures.includes('signageMode');

  function toggleRotation(on: boolean): void {
    if (on) {
      store.patchSignage({ rotationIntervalSeconds: DEFAULT_SECONDS });
    } else {
      // Clear the whole signage block when nothing's left in it. Keeps
      // exported configs tidy (no empty `signage: {}` blob lingering).
      store.patchSignage(undefined);
    }
  }

  function setInterval(raw: string): void {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, n));
    store.patchSignage({
      ...(signage ?? {}),
      rotationIntervalSeconds: clamped,
    });
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Signage</h2>
        <p className="editor__sub">
          Settings specific to unattended displays (lobby tablet, menu
          board, drive-thru order screen). Honored only when the
          <code> signageMode </code>feature is entitled at runtime — Pro tier.{' '}
          <a
            href="https://mobilauncher.com/docs/customize#schedules"
            target="_blank"
            rel="noopener noreferrer"
            className="editor__doc-link"
          >
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Profile auto-rotation</legend>
        <ToggleField
          label="Cycle through profiles"
          hint="When on, the launcher walks profiles[] in array order at the interval below. Independent of schedules — rotation wins when both are configured. Needs at least 2 profiles."
          value={rotationOn}
          onChange={toggleRotation}
        />
        {rotationOn && (
          <label className="field">
            <span className="field__label">Interval (seconds)</span>
            <input
              type="number"
              className="field__input field__input--num"
              min={MIN_SECONDS}
              max={MAX_SECONDS}
              step={5}
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              style={{ width: '6rem' }}
              data-testid="signage-rotation-interval"
            />
            <span className="field__hint">
              {MIN_SECONDS}–{MAX_SECONDS}s · {humanInterval(interval)}
            </span>
          </label>
        )}
        {rotationOn && profileCount < 2 && (
          <p className="editor__hint" data-testid="signage-needs-profiles">
            ⚠ Only one profile is configured. Add at least one more profile
            (under <strong>Profile basics</strong>) or rotation has nothing
            to cycle through.
          </p>
        )}
        {rotationOn && !signageFlagOn && (
          <p className="editor__hint" data-testid="signage-needs-flag">
            ⚠ <strong>signageMode</strong> isn't enabled in this config's
            features. Turn it on under <strong>Features</strong> or
            on-device rotation won't activate.
          </p>
        )}
      </fieldset>
    </div>
  );
}

function humanInterval(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.round(seconds / 60))}m`;
  return `${(seconds / 3600).toFixed(seconds % 3600 === 0 ? 0 : 1)}h`;
}
